import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BOOKING_GROUP_KIND_LABELS, BOOKING_GROUP_KINDS, type BookingGroupKind, type ProductBookingGroup } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { InfoHint } from '../../components/InfoHint';
import { useSession } from '../../session';
import './products.css';

interface Draft {
  name: string;
  kind: BookingGroupKind;
  accountNumber: string;
  note: string;
}
const EMPTY: Draft = { name: '', kind: 'revenue', accountNumber: '', note: '' };
const toBody = (d: Draft) => ({ name: d.name.trim(), kind: d.kind, accountNumber: d.accountNumber.trim() || null, note: d.note.trim() || null });

/**
 * Bereich „Buchungsgruppen“ der Einrichtung unter „Produkte“. Jedes Produkt gehört zu einer Gruppe, und die Gruppe legt fest, auf welches Sachkonto der Erlös gebucht wird, wenn das Produkt auf einer
 * Rechnung steht. Das Konto wählt der Steuerberater. Eine Gruppe der Art „Gutschein“ gehört zu Gutschein-Artikeln: Deren Verkauf ist kein Erlös, das Konto ist das der Verbindlichkeit.
 */
export function ProductBookingGroupsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const canWrite = can('setup.write');
  const canDelete = can('setup.delete');
  const groups = useQuery({ queryKey: ['product-booking-groups', 'manage'], queryFn: async () => (await api<{ data: ProductBookingGroup[] }>('GET', '/product-booking-groups/manage')).data });
  const refresh = () => client.invalidateQueries({ queryKey: ['product-booking-groups'] });
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState<Draft>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const fail = (e: Error) => {
    if (e instanceof ApiError && e.status === 422) setErrors(e.fields);
    else setError(e instanceof ApiError ? e.message : 'Das hat nicht geklappt.');
  };

  const create = useMutation({
    mutationFn: () => api('POST', '/product-booking-groups', toBody(draft)),
    onSuccess: () => { setDraft(EMPTY); setErrors({}); setError(null); refresh(); },
    onError: fail,
  });
  const update = useMutation({
    mutationFn: (id: string) => api('PATCH', `/product-booking-groups/${id}`, toBody(edit)),
    onSuccess: () => { setEditing(null); setErrors({}); setError(null); refresh(); },
    onError: fail,
  });
  const remove = useMutation({ mutationFn: (id: string) => api('DELETE', `/product-booking-groups/${id}`), onSuccess: () => { setError(null); refresh(); }, onError: fail });
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  const fields = (d: Draft, set: (d: Draft) => void) => (
    <div className="form-grid">
      <label className="field"><span>Name</span><input className="input" value={d.name} maxLength={100} onChange={(e) => set({ ...d, name: e.target.value })} placeholder="Zum Beispiel Fahrten 19 %" aria-invalid={!!errors.name} />{err('name')}</label>
      <label className="field">
        <span className="invoice-label">Art<InfoHint label="Art der Gruppe">Erlös: Der Betrag der Rechnungsposition ist Umsatz und wird auf das Konto gebucht. Gutschein: Ein Gutschein-Artikel verkauft ein Guthaben, das ist noch kein Umsatz. Das Konto ist das der Verbindlichkeit gegenüber dem Inhaber. Der Erlös entsteht erst bei der Einlösung, auf der Rechnung der Leistung.</InfoHint></span>
        <select className="input" value={d.kind} onChange={(e) => set({ ...d, kind: e.target.value as BookingGroupKind })}>
          {BOOKING_GROUP_KINDS.map((k) => <option key={k} value={k}>{BOOKING_GROUP_KIND_LABELS[k]}</option>)}
        </select>
      </label>
      <label className="field">
        <span className="invoice-label">Sachkonto<InfoHint label="Sachkonto">Die Kontonummer, die Ihr Steuerberater für diese Gruppe nennt, zum Beispiel 8400 für Erlöse 19 % oder 1604 für Verbindlichkeiten aus Gutscheinen im SKR03. Die Nummern hängen vom Kontenrahmen ab. Das Konto muss nicht im Kontenplan stehen, Wirkia zeigt aber den Namen, wenn es dort steht.</InfoHint></span>
        <input className="input num" inputMode="numeric" value={d.accountNumber} maxLength={8} onChange={(e) => set({ ...d, accountNumber: e.target.value })} aria-invalid={!!errors.accountNumber} />
        {err('accountNumber')}
      </label>
      <label className="field field--wide"><span>Notiz</span><input className="input" value={d.note} maxLength={500} onChange={(e) => set({ ...d, note: e.target.value })} placeholder="Zum Beispiel: so mit dem Steuerberater abgestimmt am …" /></label>
    </div>
  );

  return (
    <section className="card">
      <h2>Buchungsgruppen</h2>
      <p className="muted small">
        Jedes Produkt gehört zu einer Buchungsgruppe. Die Gruppe sagt Wirkia, auf welches Sachkonto der Erlös gebucht wird, wenn das Produkt auf einer Rechnung steht. Das Konto nennt Ihr Steuerberater.
        Produkte ohne Gruppe buchen wie bisher nach dem Steuerschlüssel der Rechnung. Gutschein-Artikel brauchen eine Gruppe der Art „Gutschein“.
      </p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {canWrite && (
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); setErrors({}); create.mutate(); }} noValidate>
          <h3 className="payroll-sub">Neue Gruppe</h3>
          {fields(draft, setDraft)}
          <div className="form-actions"><button className="btn btn--primary" type="submit" disabled={create.isPending || !draft.name.trim()}>{create.isPending ? 'Wird angelegt …' : 'Gruppe anlegen'}</button></div>
        </form>
      )}

      <table className="table table--cards">
        <thead><tr><th>Gruppe</th><th>Art</th><th>Sachkonto</th><th>Produkte</th><th><span className="sr-only">Aktionen</span></th></tr></thead>
        <tbody>
          {groups.isLoading && <tr><td colSpan={5} className="muted">Wird geladen …</td></tr>}
          {groups.data?.length === 0 && <tr><td colSpan={5} className="empty">Noch keine Buchungsgruppen. Legen Sie zum Beispiel „Erlöse 19 %“ und „Gutscheine“ an.</td></tr>}
          {groups.data?.map((g) => (
            <tr key={g.id}>
              {editing === g.id ? (
                <td colSpan={5}>
                  {fields(edit, setEdit)}
                  <div className="form-actions">
                    <button className="btn btn--primary btn--sm" type="button" disabled={update.isPending} onClick={() => { setErrors({}); update.mutate(g.id); }}>Speichern</button>
                    <button className="btn btn--secondary btn--sm" type="button" onClick={() => { setEditing(null); setErrors({}); }}>Abbrechen</button>
                  </div>
                </td>
              ) : (
                <>
                  <td data-label="Gruppe"><strong>{g.name}</strong>{g.note && <div className="muted small">{g.note}</div>}</td>
                  <td data-label="Art">{BOOKING_GROUP_KIND_LABELS[g.kind]}</td>
                  <td data-label="Sachkonto">{g.accountNumber ? <>{g.accountNumber}{g.accountName ? <span className="muted small"> · {g.accountName}</span> : <span className="muted small"> · nicht im Kontenplan</span>}</> : <span className="muted">fehlt noch</span>}</td>
                  <td data-label="Produkte">{g.productCount}</td>
                  <td>
                    {canWrite && <button className="btn btn--secondary btn--sm" type="button" onClick={() => { setEditing(g.id); setEdit({ name: g.name, kind: g.kind, accountNumber: g.accountNumber ?? '', note: g.note ?? '' }); setErrors({}); }}>Ändern</button>}{' '}
                    {canDelete && <button className="btn btn--ghost btn--sm" type="button" disabled={remove.isPending} onClick={() => { if (window.confirm(`Buchungsgruppe „${g.name}“ löschen?`)) remove.mutate(g.id); }}>Löschen</button>}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
